import { devModeMocks } from "@api/core-api-utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { teamAdapter } from "@store/teams/teams-slice";
/**
 * Fetches teams from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if any Core API client request fails
 */
export const fetchTeams = createAsyncThunk("teams/fetchTeams", async ({
  searchText,
  memberIdentity
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    },
    app: {
      isDevModeEnabled
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to search for teams");
  }

  // "memberIdentity" is not yet part of the published GetTeamsPayload type,
  // so widen the payload type to forward it as a query param.
  const payload = {
    search: searchText,
    ...(memberIdentity ? {
      memberIdentity
    } : {})
  };
  try {
    return await faroCoreApiClient.teams.getTeams({
      companyId: selectedSdbCompanyId,
      payload
    });
  } catch (error) {
    if (isDevModeEnabled) {
      return devModeMocks.teams;
    }
    throw error;
  }
}, {
  condition: ({
    searchText,
    memberIdentity
  }, {
    getState
  }) => {
    const state = getState();

    // Member-scoped fetch: only skip when the same member's teams are already
    // loaded and there is no search text to apply.
    if (memberIdentity) {
      return !(state.teams.memberTeams.status === "succeeded" && state.teams.memberTeams.memberIdentity === memberIdentity && !searchText);
    }

    // If succeeded fetching status means the data is already fetched once and no need to do it again if there is no search text
    // and there is no search text, do not fetch again
    if (state.teams.fetching.status === "succeeded" && !searchText) {
      return false;
    }
    return true;
  }
});

/**
 * Fetches the details of a team
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamDetails = createAsyncThunk("teams/fetchTeamDetails", async ({
  teamId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch team details");
  }
  return await faroCoreApiClient.teams.fetchTeamById({
    companyId: selectedSdbCompanyId,
    teamId
  });
});

/**
 * Fetches the details of a team as member
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamAsMember = createAsyncThunk("teams/fetchTeamAsMember", async ({
  teamId
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetch the team");
  }

  /**
   * Fetches the details of a team as member. This is the way to get all the groups and projects that the team is part of.
   * Backend will accept the team ID in fetching members route and return it in the member type
   */
  return await faroCoreApiClient.company.getMemberDetailsInCompany({
    companyId: selectedSdbCompanyId,
    identity: teamId
  });
});

/**
 * Creates a team in the backend and adds it to the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const createTeam = createAsyncThunk("teams/createTeam", async ({
  companyId,
  teamName,
  description
}) => {
  const team = await faroCoreApiClient.teams.createNewTeam({
    companyId,
    payload: {
      name: teamName,
      description
    }
  });
  return team;
});

/**
 * Fetches team members from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchTeamMembers = createAsyncThunk("teams/fetchTeamMembers", async ({
  companyId,
  teamId,
  start,
  sortBy,
  search
}) => {
  const result = await faroCoreApiClient.teams.fetchTeamMembers({
    companyId,
    teamId,
    start,
    sortBy,
    search
  });
  return {
    teamId,
    members: result.data,
    next: result.page.next,
    totalCount: result.page.total
  };
}, {
  condition: ({
    start,
    sortBy,
    search
  }, api) => {
    const state = api.getState();
    if (start) {
      return true;
    }
    const isSameSortBy = state.teams.selectedTeam.membersSortBy === (sortBy ?? null);
    const isSameSearch = state.teams.selectedTeam.membersSearch === (search ?? null);

    // Skip fetching if we already have the data and not fetching again
    if (state.teams.fetching.isFetchingTeamMembers === "succeeded" && isSameSortBy && isSameSearch) {
      return false;
    }
    return true;
  }
});

/**
 * Invite members to a team
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const addMembersToTeam = createAsyncThunk("teams/addMembersToTeam", async ({
  companyId,
  teamId,
  members
}) => {
  const addMembersResponse = await faroCoreApiClient.teams.addMembersToTeam({
    companyId,
    teamId,
    payload: {
      members
    }
  });
  return {
    ...addMembersResponse,
    teamId
  };
});
/**
 * Update team details
 * @throws {Error} if there is no selected company ID or team ID or if team is not found in the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateTeamDetails = createAsyncThunk("teams/updateTeamDetails", async ({
  teamId,
  payload
}, {
  getState
}) => {
  const {
    teams,
    sdbCompany
  } = getState();

  /**
   * The ID of the team that is going to be updated.
   * If teamId is not provided, the selectedTeamId from store is selected
   */
  const updatingTeamId = teamId || teams.selectedTeam.id;
  if (!sdbCompany.selectedSdbCompanyId) {
    throw new Error("No companyId exist to updateTeamDetails");
  }
  if (!updatingTeamId) {
    throw new Error("No teamId exist to updateTeamDetails");
  }
  const team = teamAdapter.getSelectors().selectById(teams, updatingTeamId);
  if (!team) {
    throw new Error("Team not found");
  }
  const updatedTeam = await faroCoreApiClient.teams.updateTeam({
    companyId: sdbCompany.selectedSdbCompanyId,
    teamId: updatingTeamId,
    payload
  });
  return updatedTeam;
});

/**
 * Remove a member from the team using the backend,
 * and then removes it from team in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMembersFromTeam = createAsyncThunk("teams/removeMembersFromTeam", async ({
  companyId,
  teamId,
  memberIds
}) => {
  await faroCoreApiClient.teams.removeMembersFromTeam({
    companyId,
    teamId,
    payload: {
      members: memberIds
    }
  });
  return {
    teamId,
    companyId,
    memberIds
  };
});

/**
 * Delete a team
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const deleteTeam = createAsyncThunk("teams/deleteTeam", async ({
  companyId,
  teamId
}) => {
  await faroCoreApiClient.teams.deleteTeam({
    companyId,
    teamId
  });
  return teamId;
});